package co.maytes.standupbot.dashboard.features.membersSettings

import co.maytes.standupbot.dashboard.features.settings.MemberAction
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.slack.SlackDirectoryUserDto
import co.maytes.standupbot.shared.dto.workspace.InviteDeliveryDto
import co.maytes.standupbot.shared.dto.workspace.InviteLinkDto
import co.maytes.standupbot.shared.dto.workspace.MemberStatusDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceMemberDto
import com.kttipay.mvi.api.MVIAction
import com.kttipay.mvi.api.MVIIntent
import com.kttipay.mvi.api.MVIState

object MembersSettingsContract {
    sealed interface Status {
        data object Idle : Status
        data object Loading : Status
        data object Ready : Status
        data class Error(val error: ApiError) : Status
    }

    enum class InviteTab { SLACK, EMAIL, LINK }

    data class InviteOutcome(
        val label: String,
        val delivery: InviteDeliveryDto? = null,
        val acceptUrl: String? = null,
        val error: String? = null,
    )

    data class State(
        val workspaceId: String = "",
        val canManage: Boolean = false,
        val status: Status = Status.Idle,
        val members: List<WorkspaceMemberDto> = emptyList(),
        val search: String = "",
        val inviteOpen: Boolean = false,
        val inviteTab: InviteTab = InviteTab.SLACK,
        val inviteSubmitting: Boolean = false,
        val inviteError: String? = null,
        val inviteOutcomes: List<InviteOutcome> = emptyList(),
        val inviteLinks: List<InviteLinkDto> = emptyList(),
        val inviteLinksLoading: Boolean = false,
        val inviteLinksLoaded: Boolean = false,
        val inviteLinksFailed: Boolean = false,
        val directory: List<SlackDirectoryUserDto> = emptyList(),
        val directoryLoading: Boolean = false,
        val directoryFailed: Boolean = false,
        val freshInviteUrls: Map<String, String> = emptyMap(),
        val pendingDisable: WorkspaceMemberDto? = null,
        val pendingRestore: WorkspaceMemberDto? = null,
        val pendingErase: WorkspaceMemberDto? = null,
        val transferTo: WorkspaceMemberDto? = null,
        val transferSubmitting: Boolean = false,
        val transferError: String? = null,
        val transferNeedsSignIn: Boolean = false,
        val showDisabled: Boolean = true,
        val pendingInviteRevocation: WorkspaceMemberDto? = null,
        val linkLinearFor: WorkspaceMemberDto? = null,
        val linkGithubFor: WorkspaceMemberDto? = null,
        val actionError: String? = null,
    ) : MVIState {
        val ownerCount: Int get() = members.count { it.role == RoleDto.OWNER && it.status == MemberStatusDto.ACTIVE }

        val disabledCount: Int get() = members.count { it.status == MemberStatusDto.DISABLED }

        val displayMembers: List<WorkspaceMemberDto>
            get() {
                val byId = directory.associateBy { it.slackUserId }
                return members
                    .map { member ->
                        val known = member.slackUserId?.let { byId[it] }
                        if (member.status == MemberStatusDto.INVITED && known != null) {
                            member.copy(displayName = known.displayName, avatarUrl = known.avatarUrl ?: member.avatarUrl)
                        } else {
                            member
                        }
                    }
                    .filter { showDisabled || it.status != MemberStatusDto.DISABLED }
                    .sortedBy { it.status == MemberStatusDto.DISABLED }
            }

        val filtered: List<WorkspaceMemberDto>
            get() {
                val q = search.trim().lowercase()
                val all = displayMembers
                if (q.isEmpty()) return all
                return all.filter {
                    it.displayName.lowercase().contains(q) ||
                        (it.email?.lowercase()?.contains(q) == true)
                }
            }

        val memberSlackIds: Set<String>
            get() = members.filter { it.status == MemberStatusDto.ACTIVE }.mapNotNull { it.slackUserId }.toSet()

        val invitedSlackIds: Set<String>
            get() = members.filter { it.status == MemberStatusDto.INVITED }.mapNotNull { it.slackUserId }.toSet()
    }

    sealed interface Intent : MVIIntent {
        data class Load(val workspaceId: String, val canManage: Boolean) : Intent
        data class SetSearch(val value: String) : Intent
        data object OpenInvite : Intent
        data object OpenInviteLinks : Intent
        data object LoadInviteLinks : Intent
        data object CloseInvite : Intent
        data class SetInviteTab(val tab: InviteTab) : Intent
        data class SendSlackInvites(val slackUserIds: List<String>, val role: RoleDto) : Intent
        data class SendEmailInvite(val email: String, val role: RoleDto) : Intent
        data class CreateInviteLink(
            val role: RoleDto,
            val expiresInDays: Int,
            val maxUses: Int?,
            val label: String?,
        ) : Intent
        data class RevokeInviteLink(val linkId: String) : Intent
        data object ClearInviteOutcomes : Intent
        data object ReloadDirectory : Intent
        data class OnMemberAction(val member: WorkspaceMemberDto, val action: MemberAction) : Intent
        data object CancelDisable : Intent
        data object ConfirmDisable : Intent
        data object CancelRestore : Intent
        data object ConfirmRestore : Intent
        data object CancelErase : Intent
        data object ConfirmErase : Intent
        data object CancelTransfer : Intent
        data class ConfirmTransfer(val confirmWorkspaceName: String) : Intent
        data class SetShowDisabled(val value: Boolean) : Intent
        data object CancelInviteRevocation : Intent
        data object ConfirmInviteRevocation : Intent
        data object CloseLinkLinear : Intent
        data class SubmitLinkLinear(val linearUserId: String?) : Intent
        data object CloseLinkGithub : Intent
        data class SubmitLinkGithub(val githubLogin: String?) : Intent
    }

    sealed interface Action : MVIAction {
        data class Notify(val message: String, val success: Boolean) : Action
        data object OwnershipTransferred : Action
    }
}
